import React from "react";
import { Box, useTheme } from "@mui/joy";
import Popper from "@mui/material/Popper";
import useMediaQuery from "@mui/material/useMediaQuery";
import { unstable_useForkRef as useForkRef } from "@mui/utils";
import { useExcursionCardTooltipState } from "../hooks/useExcursionCardTooltipState";
import { excursionCardTooltipPopperModifiers } from "./excursionTooltipHelpers";
import ExcursionTooltipFloaterContent from "./ExcursionTooltipFloaterContent";
import type {
  BookingStatus,
  Excursion,
  ExcursionChipDropPending,
  GroupLabels,
  ExcursionTemplateLimitsItem,
} from "api/excursions/Excursion.typed";

export type ExcursionCardTooltipProps = {
  excursion: Excursion;
  canEdit: boolean;
  groupLabels?: GroupLabels[];
  makeGroupChipDropHandlers?: (targetExcursionId: number, targetGroupIndex: number) => object;
  forceOpen?: boolean;
  forceClose?: boolean;
  planningClientChipDragActive?: boolean;
  suppressHoverOpen?: boolean;
  ignoreHoverClosestSelector?: string;
  onClientDragStart?: (sourceExcursionId: number) => void;
  onClientDragEnd?: () => void;
  exclusivePlanningTooltipExcursionId?: number | null;
  onExclusivePlanningTooltipIntent?: (excursionId: number) => void;
  onExclusivePlanningTooltipRelease?: (excursionId: number) => void;
  onTooltipClick?: (e: React.MouseEvent) => void;
  excursionTemplatesList?: ExcursionTemplateLimitsItem[];
  bookingStatusMap?: Record<number, BookingStatus>;
  getCustomerInProgressTrainingsTooltip?: (customerId: number) => string | null;
  onPlanningForcedPopoverDismiss?: () => void;
  clientChipDragSourceEnabled?: boolean;
  chipDropPending?: ExcursionChipDropPending | null;
  children: React.ReactElement;
};

export default function ExcursionCardTooltip({
  excursion,
  canEdit,
  groupLabels,
  makeGroupChipDropHandlers,
  forceOpen,
  forceClose,
  planningClientChipDragActive = false,
  suppressHoverOpen,
  ignoreHoverClosestSelector,
  onClientDragStart,
  onClientDragEnd,
  exclusivePlanningTooltipExcursionId = null,
  onExclusivePlanningTooltipIntent,
  onExclusivePlanningTooltipRelease,
  onTooltipClick,
  excursionTemplatesList,
  bookingStatusMap,
  getCustomerInProgressTrainingsTooltip,
  onPlanningForcedPopoverDismiss,
  clientChipDragSourceEnabled = true,
  chipDropPending = null,
  children,
}: ExcursionCardTooltipProps) {
  const theme = useTheme();
  const useClickMode = useMediaQuery(theme.breakpoints.down("md"));

  const tt = useExcursionCardTooltipState({
    excursionId: excursion.id,
    forceOpen,
    forceClose,
    suppressHoverOpen,
    ignoreHoverClosestSelector,
    useClickMode,
    exclusivePlanningTooltipExcursionId,
    onExclusivePlanningTooltipIntent,
    onExclusivePlanningTooltipRelease,
  });

  const popperOpen = Boolean(!forceClose && tt.isOpen && tt.anchorEl);
  const floaterPassesPointerThroughWhileChipDrag = Boolean(
    planningClientChipDragActive && popperOpen,
  );

  const handleTooltipContentMouseLeave = React.useCallback(
    (event: React.MouseEvent) => {
      tt.onTooltipMouseLeave(event);
      if (!onPlanningForcedPopoverDismiss) return;
      const next = event.relatedTarget;
      if (next instanceof Node && tt.anchorEl?.contains(next)) return;
      if (next instanceof Element && next.closest("[data-excursion-tooltip]")) return;

      window.requestAnimationFrame(() => {
        const { clientX, clientY } = event;

        try {
          if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
            onPlanningForcedPopoverDismiss();

            return;
          }

          const top = document.elementFromPoint(clientX, clientY);
          if (top == null) {
            onPlanningForcedPopoverDismiss();

            return;
          }

          if (tt.anchorEl?.contains(top)) return;
          if (top.closest("[data-excursion-tooltip]")) return;
          onPlanningForcedPopoverDismiss();
        } catch {
          onPlanningForcedPopoverDismiss();
        }
      });
    },
    [onPlanningForcedPopoverDismiss, tt.anchorEl, tt.onTooltipMouseLeave],
  );

  const hoverContent = (
    <Box aria-hidden={floaterPassesPointerThroughWhileChipDrag ? true : undefined}>
      <ExcursionTooltipFloaterContent
        excursion={excursion}
        canEdit={canEdit}
        groupLabels={groupLabels}
        makeGroupChipDropHandlers={makeGroupChipDropHandlers}
        excursionTemplatesList={excursionTemplatesList}
        bookingStatusMap={bookingStatusMap}
        getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
        closeTooltipAfterBookingAction={tt.closeTooltipAfterBookingAction}
        onClientDragStart={onClientDragStart}
        onClientDragEnd={onClientDragEnd}
        clientChipDragSourceEnabled={clientChipDragSourceEnabled}
        reconnectDropSurfacePointerEventsInsideFloater={floaterPassesPointerThroughWhileChipDrag}
        onGradientCardNavigate={
          onTooltipClick
            ? (e: React.MouseEvent) => {
                onTooltipClick(e);
              }
            : undefined
        }
        tooltipContentId={tt.tooltipContentId}
        onTooltipContentMouseEnter={tt.onTooltipMouseEnter}
        onTooltipContentMouseLeave={
          onPlanningForcedPopoverDismiss
            ? handleTooltipContentMouseLeave
            : (ev: React.MouseEvent) => tt.onTooltipMouseLeave(ev)
        }
        contentRole={useClickMode ? "dialog" : "tooltip"}
        ariaModal={useClickMode ? false : undefined}
        chipDropPending={chipDropPending}
      />
    </Box>
  );

  const childRef = (children as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;
  const triggerRef = useForkRef(childRef, tt.setAnchorRef);

  const trigger = React.cloneElement(children, {
    ref: triggerRef,
    ...tt.triggerAriaProps,
    onClick: (e: React.MouseEvent) => {
      tt.onTriggerClick(e);
      (children.props as { onClick?: (e: React.MouseEvent) => void }).onClick?.(e);
    },
    onMouseEnter: (e: React.MouseEvent) => {
      tt.onTriggerMouseEnter(e);
      (children.props as { onMouseEnter?: (e: React.MouseEvent) => void }).onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      tt.onTriggerMouseLeave(e);
      (children.props as { onMouseLeave?: (e: React.MouseEvent) => void }).onMouseLeave?.(e);
    },
    onMouseMove: (e: React.MouseEvent) => {
      tt.onTriggerMouseMove(e);
      (children.props as { onMouseMove?: (e: React.MouseEvent) => void }).onMouseMove?.(e);
    },
  });

  return (
    <>
      {trigger}
      <Popper
        open={popperOpen}
        anchorEl={tt.anchorEl}
        placement="top"
        modifiers={excursionCardTooltipPopperModifiers}
        popperOptions={{ strategy: "fixed" }}
        sx={{
          zIndex: `calc(${theme.vars.zIndex.modal} + 1)`,
          ...(floaterPassesPointerThroughWhileChipDrag
            ? {
                opacity: 0,
                pointerEvents: "none",
                transition: "none",
                maxHeight: "55dvh",
              }
            : {
                pointerEvents: "auto",
                maxHeight: "calc(100dvh - 16px)",
              }),
          maxWidth: { xs: "95vw", sm: 600 },
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* aria-hidden delegated to TooltipFloaterContent tooltip bridge when invisible */}
        {hoverContent}
      </Popper>
    </>
  );
}
